import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadCurrent } from 'app/state/utils/generation'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import {
  dealScreenLoadFailed,
  getDealScreen,
  startLoadingDealScreen,
  stopLoadingDealScreen,
} from './actions'
import { DealScreenState } from './reducer'

type GetState = () => { dealScreen: DealScreenState }

/** One request at a time: a strict-mode double mount, or a page effect firing during a load, joins it. */
let inFlight: Promise<unknown> | null = null

/**
 * The whole Deals grid in one request: every deal across its three states,
 * each amendment in flight shown in place of the deal it amends, borrower and
 * admin names and the Signing Portal holds already on the row. The backend
 * reads five templates in one PQS statement plus one small name lookup, so the
 * screen no longer loads the deal, amendment and entity lists just to stitch a
 * row.
 *
 * The response carries the store's generation at the time of the request; if
 * a write bumped it meanwhile the reducer drops the rows and this reads once
 * more (utils/generation.ts). On failure the store keeps what it had, the user
 * is told, and the loader still stops.
 */
export const loadDealScreen = () => (dispatch: Dispatch, getState?: GetState) => {
  if (inFlight) return inFlight
  const generation = () => getState?.().dealScreen.generation ?? 0
  inFlight = loadCurrent(generation, g => load(g, dispatch)).finally(() => {
    // in flight is cleared BEFORE the loader stops: the grid's "reload once
    // while stale" effect fires on the loader stopping, and must start a new
    // read rather than join this finished one
    inFlight = null
    dispatch(stopLoadingDealScreen())
  })
  return inFlight
}

const load = async (generation: number, dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  try {
    dispatch(startLoadingDealScreen())
    const response = await client.get(`${apiServerUrl}/api/deal-management/screen`)
    return dispatch(getDealScreen(response.data, generation))
  } catch (e: any) {
    dispatch(dealScreenLoadFailed())
    ErrorNotification({
      title: 'Could not load deals',
      message: e?.response?.data?.error ?? 'The Deals grid could not be loaded. Reload the page to try again.',
    })
  }
}
